import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Card, Grid, Link } from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  ColumnSettings,
  ColumnSettingsDTO,
  getColumnSettings,
  saveColumnSettings,
  VendorDTO,
  VendorRequestDTO,
  getVendorsListNew,
  updateVendorStatusById,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  formatLocation,
  getDateUsingFormat,
  SearchBar,
  WsButton,
  WsColors,
  WsIconMoreVer,
  WsListingLoader,
  WsTitleBanner,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import { getCommonGridStyles } from '../../../features/clientListingPage/helper/ClientListingHelper';
import { CustomToolbar } from '../../../features/invoicing/components/DataGridCustomToolBar';

const VendorsList = () => {
  const [settings, setSettings] = useState<GridColumnVisibilityModel | null>(null);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [vendorListData, setVendorListData] = useState<VendorDTO[]>([]);
  const tableData: Record<string, unknown>[] = [];
  const methods = useForm();
  const { handleSubmit } = methods;
  const navigate = useNavigate();
  const onSearch = () => {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getColumnSettings(ColumnSettings.VENDOR_LISTING);
        setSettings(res.data.settings);
      } catch (error) {
        setSettings({
          name: true,
          email: true,
          createdBy: true,
          createdOn: true,
          location: true,
          legalName: false,
          address: false,
          active: false,
          registrationId: false,
        });
      }
    };
    if (settings === null) {
      fetchData();
    }
  }, [settings]);

  const getVendorDataList = useCallback(
    async (status?: string[]) => {
      setIsLoading(true);
      try {
        const searchText = methods.watch('query') ?? '';
        const query: string = searchText ?? '';
        const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
        const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
        const vendorReqDTO: VendorRequestDTO = {
          status: status ?? [],
          query: query,
          pageNo: pageNo,
          pageSize: pageSize,
          sortBy: sortBy ?? '',
          sortOrder: sortOrder ?? '',
        };
        const response = await getVendorsListNew(vendorReqDTO);
        setTotalRecord(response.data.totalElements);
        setVendorListData(response.data.content);
      } catch (err) {
        console.log(err);
      }
      setIsLoading(false);
    },
    [methods, pageNo, pageSize, sortModel]
  );

  useEffect(() => {
    getVendorDataList();
  }, [getVendorDataList]);

  const onColumnChange = async (data: GridColumnVisibilityModel) => {
    const settingsPayload: ColumnSettingsDTO = {
      settings: data,
      tableKey: ColumnSettings.VENDOR_LISTING,
    };
    await saveColumnSettings(settingsPayload);
  };

  const columnsName: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Vendor Name',
      flex: 1,
      renderCell: (params) => {
        return (
          <Link
            component={RouterLink}
            sx={{ display: 'flex' }}
            underline="none"
            to={`${params.id}`}
          >
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              bold
              color={WsTypoColors.PRIMARY}
              cursor="pointer"
            >
              {params.row['name']}
            </WsTypography>
          </Link>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
    },
    {
      field: 'legalName',
      headerName: 'Legal Name',
      flex: 0.8,
    },
    {
      field: 'registrationId',
      headerName: 'Registration Id',
      flex: 0.8,
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 0.8,
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.2,
      sortable: false,
      renderCell: (params) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <Box p={1}>
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              cursor="pointer"
              onClick={() => navigate(`/vendorslist/edit/${params.id}`)}
            >
              Edit
            </WsTypography>
          </Box>
          <Box p={1}>
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              cursor="pointer"
              onClick={() =>
                handleVendorContactStatus(
                  params.row.id,
                  params.row.status == 'Active' ? false : true
                )
              }
            >
              {`Mark as ${params?.row?.status === 'Active' ? 'Inactive' : 'Active'}`}
            </WsTypography>
          </Box>
        </PopoverOnClick>
      ),
    },
  ];

  vendorListData.forEach((vendorData) => {
    tableData.push({
      name: vendorData.name,
      email: vendorData.email,
      legalName: vendorData.legalName,
      registrationId: vendorData.registrationId,
      address: vendorData.address,
      location: formatLocation(vendorData.location),
      id: vendorData.id,
      status: vendorData.active ? 'Active' : 'Inactive',
      createdOn:
        vendorData?.createdOn != undefined
          ? getDateUsingFormat(vendorData.createdOn?.toString(), 'DD-MMM-YY')
          : '',
      createdBy: vendorData.createdByName,
    });
  });

  const handleVendorContactStatus = async (id: string, markStatus: boolean) => {
    await updateVendorStatusById(id, markStatus);
    getVendorDataList();
  };

  return (
    <Grid container sx={{ display: 'flex' }}>
      <Grid item xs={12}>
        <WsTitleBanner title="Vendors" backButton={false} showBetaIcon={true} />
      </Grid>
      <Grid item xs={12} sx={{ ml: 8 }} container justifyContent="flex-end">
        <Grid item xs={2} p={1} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <WsButton
            fullWidth
            onClick={() => navigate('/vendorslist/create')}
            variant="contained"
            size="large"
            startIcon={<AddOutlinedIcon />}
          >
            Add Vendor
          </WsButton>
        </Grid>
      </Grid>
      <Grid sx={{ backgroundColor: WsColors.TheBlues50 }} container p={1} borderRadius={1.5}>
        <Grid item xs={12} justifyContent="flex-end" pt={2}>
          <FormProvider {...methods}>
            <Grid
              container
              sx={{ display: 'flex' }}
              component="form"
              onSubmit={handleSubmit(onSearch)}
            >
              <Grid item xs={9} p={1}>
                <Card sx={{ p: 0.5 }}>
                  <SearchBar name="query" placeholder="Search Vendor" />
                </Card>
              </Grid>
              <Grid item xs={1.5} p={1}>
                <WsButton fullWidth variant="outlined" size="large">
                  Clear
                </WsButton>
              </Grid>
              <Grid item xs={1.5} p={1}>
                <WsButton
                  fullWidth
                  variant="outlined"
                  size="large"
                  type="submit"
                  startIcon={<SearchIcon />}
                >
                  Search
                </WsButton>
              </Grid>
              <Grid xs={12} bgcolor={WsColors.White} mt={1}>
                {settings && !isLoading ? (
                  <DataGrid
                    autoHeight={true}
                    components={{ Toolbar: CustomToolbar }}
                    columns={columnsName}
                    rows={tableData}
                    onPageChange={(page) => setPageNo(page)}
                    page={pageNo}
                    paginationMode="server"
                    pageSize={pageSize}
                    rowCount={totalRecord}
                    rowsPerPageOptions={[25, 50, 75, 100]}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    sortModel={sortModel}
                    onSortModelChange={(newSortModel) => {
                      setPageNo(0);
                      setSortModel(newSortModel);
                    }}
                    columnVisibilityModel={settings}
                    onColumnVisibilityModelChange={(data) => {
                      onColumnChange(data);
                      setSettings(data);
                    }}
                    sortingMode="server"
                    getRowHeight={() => 'auto'}
                    sx={getCommonGridStyles()}
                  />
                ) : isLoading ? (
                  <Grid item xs={12}>
                    <WsListingLoader />
                  </Grid>
                ) : (
                  <NoDataFoundCard
                    text="No results found. Would you like to add a new Vendor?"
                    icon={<EmptyTeamsAndUsersIcon />}
                  />
                )}
              </Grid>
            </Grid>
          </FormProvider>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default VendorsList;
